import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import {
  faArrowLeft,
  faBadgeCheck,
  faChartPie,
  faCircleDollarToSlot,
  faEarth,
  faFaceViewfinder,
  faFileLines,
  faGears,
  faListMusic,
  faQrcode,
  faRankingStar,
  faReceipt,
  faRectangleAd,
  faScrewdriverWrench,
  faShirt,
  faShoePrints,
  faSignalStream,
  faTruckFast,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, NavigationDrawerContent, Stack } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiGetCurrentFeaturePlanResponseBody,
  IFeaturePlanFeatures,
} from '~/lib/api/modules/Apps';
import type { ISidebarSectionItem } from './SidebarSection';
import { NavigationDrawerDestination } from '~/components/atoms/NavigationDrawerDestination';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { useAppCurrentFeaturePlanQuery } from '~/lib/api/modules/Apps/hooks/useAppCurrentFeaturePlanQuery';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUserContentQuery } from '~/lib/api/modules/UserContent/hook/useUserContentQuery';
import { SidebarSection } from './SidebarSection';

export type IAppSidebarContentProps = IContainerProps & {
  appId: string;
};

type IGetItemsProps = {
  baseUrl: string;
  t: TFunction<'translation', undefined>;
  locale: string;
  plan?: IApiGetCurrentFeaturePlanResponseBody;
  features?: IFeaturePlanFeatures;
  userContentToReviewCount?: number;
};

const getItems = ({
  baseUrl,
  t,
  locale,
  plan,
  features,
  userContentToReviewCount,
}: IGetItemsProps): Array<ISidebarSectionItem> => [
  {
    label: t('AppSidebarContent.managePlan.label'),
    supportingText: t('AppSidebarContent.managePlan.supportingText', {
      name: plan?.name[locale],
    }),
    faLeadingIcon: faReceipt,
    href: `${baseUrl}/settings/subscription`,
    hidden: !plan?.tags?.includes('free'),
  },
  {
    label: t('common.onboarding'),
    faLeadingIcon: faShoePrints,
    href: `${baseUrl}/onboarding`,
  },
  {
    label: t('AppSidebarContent.content'),
    faLeadingIcon: faFileLines,
    href: `${baseUrl}/content`,
    matchPatterns: [
      `${baseUrl}`,
      `${baseUrl}/content/*`,
      `${baseUrl}/articles/*`,
      `${baseUrl}/links/*`,
      `${baseUrl}/categories/*`,
    ],
  },
  {
    label: t('AppSidebarContent.moderation'),
    faLeadingIcon: faFaceViewfinder,
    href: `${baseUrl}/moderation`,
    trailing: <Badge value={userContentToReviewCount} />,
    matchPatterns: [`${baseUrl}/moderation`],
  },
  {
    label: t('AppSidebarContent.playlists'),
    faLeadingIcon: faListMusic,
    href: `${baseUrl}/playlists`,
    matchPatterns: [`${baseUrl}/playlists/*`],
    restrictions: {
      proFeature: !features?.playlists,
    },
  },
  {
    label: t('AppSidebarContent.crowd'),
    faLeadingIcon: faEarth,
    href: `${baseUrl}/community`,
    restrictions: {
      proFeature: !features?.crowd,
    },
  },
  {
    label: t('AppSidebarContent.streaming'),
    faLeadingIcon: faSignalStream,
    href: `${baseUrl}/liveStreams`,
    restrictions: {
      proFeature: !features?.liveStreams,
    },
  },
  {
    label: t('AppSidebarContent.advertisements'),
    faLeadingIcon: faRectangleAd,
    href: `${baseUrl}/ads`,
  },
  {
    label: t('AppSidebarContent.polls'),
    faLeadingIcon: faChartPie,
    href: `${baseUrl}/polls`,
    restrictions: {
      proFeature: !features?.polls,
    },
  },
  {
    label: t('AppSidebarContent.iapPolls'),
    faLeadingIcon: faCircleDollarToSlot,
    href: `${baseUrl}/iapPolls`,
    restrictions: {
      proFeature: !features?.iapPolls,
    },
  },
  {
    label: t('AppSidebarContent.leaderboard'),
    faLeadingIcon: faRankingStar,
    href: `${baseUrl}/leaderboard`,
    restrictions: {
      proFeature: !features?.leaderboardWp,
    },
  },
  {
    label: t('AppSidebarContent.merchandising'),
    faLeadingIcon: faShirt,
    href: `${baseUrl}/merch`,
  },
  {
    label: t('AppSidebarContent.badges'),
    faLeadingIcon: faBadgeCheck,
    href: `${baseUrl}/badges`,
    restrictions: {
      proFeature: !features?.badges,
    },
  },
  {
    label: t('AppSidebarContent.settings'),
    faLeadingIcon: faGears,
    href: `${baseUrl}/settings`,
    matchPatterns: [`${baseUrl}/settings`, `${baseUrl}/settings/*`],
  },
  {
    label: t('AppSidebarContent.builds'),
    faLeadingIcon: faTruckFast,
    matchPatterns: [`${baseUrl}/builds/*`],
    children: [
      {
        label: t('AppSidebarContent.ios-builds'),
        faLeadingIcon: faApple,
        href: `${baseUrl}/builds/ios`,
      },
      {
        label: t('AppSidebarContent.android-builds'),
        faLeadingIcon: faAndroid,
        href: `${baseUrl}/builds/android`,
      },
    ],
  },
  {
    label: t('AppSidebarContent.tools'),
    faLeadingIcon: faScrewdriverWrench,
    matchPatterns: [`${baseUrl}/tools`],
    children: [
      {
        label: t('AppSidebarContent.smartLink'),
        faLeadingIcon: faQrcode,
        href: `${baseUrl}/tools/smartLink`,
      },
    ],
  },
];

export const AppSidebarContent: React.FC<IAppSidebarContentProps> = (props) => {
  const { appId, ...other } = props;
  const { t, i18n } = useTranslation();
  const appQuery = useAppQuery({ appId });
  const userContentQuery = useUserContentQuery({
    appId,
    body: {
      reviewed: false,
    },
  });
  const currentFeaturePlanQuery = useAppCurrentFeaturePlanQuery({
    appId,
  });

  const plan = currentFeaturePlanQuery.data;
  const organizationId = appQuery.data?.organization?._id;
  const features = currentFeaturePlanQuery.data?.features;
  const baseUrl = `/apps/${appId}`;
  const userContentToReviewCount = userContentQuery.data?.totalCount;

  const items = useMemo(
    () =>
      currentFeaturePlanQuery.isLoading
        ? undefined
        : getItems({
            baseUrl,
            t,
            locale: i18n.language,
            plan,
            features,
            userContentToReviewCount,
          }),
    [
      currentFeaturePlanQuery.isLoading,
      baseUrl,
      t,
      i18n,
      plan,
      features,
      userContentToReviewCount,
    ],
  );

  return (
    <Stack gap={6}>
      <NavigationDrawerContent.DestinationList {...other}>
        <NavigationDrawerDestination
          href={organizationId ? `/organizations/${organizationId}` : '/apps'}
          leadingIcon={<FontAwesomeIcon icon={faArrowLeft} fixedWidth />}
          active={false}
        >
          {organizationId
            ? t('AppSidebarContent.back-to-organization')
            : t('AppSidebarContent.back-to-applications')}
        </NavigationDrawerDestination>
      </NavigationDrawerContent.DestinationList>

      {appQuery.isError ? <ErrorMessage error={appQuery.error} /> : null}

      <SidebarSection
        label={t('AppSidebarContent.my-application')}
        items={items}
        loading={currentFeaturePlanQuery.isLoading}
      />
    </Stack>
  );
};
